import { useMemo } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { mdiLoading, mdiShieldLockOutline } from '@mdi/js'
import { PageHeader } from '../shared/PageHeader'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { EmptyState } from '../ui/empty-state'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useContactsByRetention, useDocumentsByRetention, useMeasures } from '../../hooks/useDataProtection'
import { RetentionBadge } from './RetentionBadge'
import { AnonymizeContactAction, DocumentPurgeAction, ErasureRequestsSection, ExtendRetentionAction } from './Erasure'

/**
 * The DPO screen (spec 2026-10-03-02, lot 3): gaps and urgencies first, then
 * the retention queue (each due item handled one by one: purge, anonymize,
 * or extend by a hold, lot 4), the erasure requests and the active measures.
 */
export function DataProtectionPage() {
    const { org } = useParams({ strict: false }) as { org: string }
    const { t, i18n } = useTranslation('data-protection')
    const navigate = useNavigate()
    useBreadcrumbs([
        { title: t('common:nav.home'), path: `/${org}` },
        { title: t('common:nav.settings'), path: `/${org}/settings` },
        { title: t('title') },
    ])

    const docsUndefined = useDocumentsByRetention(org, 'undefined')
    const docsDue = useDocumentsByRetention(org, 'due')
    const contactsUndefined = useContactsByRetention(org, 'undefined')
    const contactsDue = useContactsByRetention(org, 'due')
    const measures = useMeasures(org)
    const overdueHolds = useMemo(() => (measures.data ?? []).filter((m) => m.review_overdue), [measures.data])
    const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString(i18n.language) : '—')

    const tiles = [
        { key: 'documents_undefined', count: docsUndefined.data?.total },
        { key: 'contacts_undefined', count: contactsUndefined.data?.total },
        { key: 'holds_to_review', count: overdueHolds.length },
        { key: 'documents_due', count: docsDue.data?.total },
        { key: 'contacts_due', count: contactsDue.data?.total },
    ]
    const loading = docsDue.isLoading || contactsDue.isLoading || measures.isLoading
    const queueEmpty = !docsDue.data?.items.length && !contactsDue.data?.items.length

    return (
        <div data-testid="data-protection-page">
            <PageHeader
                className="mb-5"
                icon={mdiShieldLockOutline}
                title={t('title')}
                description={t('subtitle')}
            />

            <section className="mb-6" data-testid="dp-urgencies">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('urgencies.title')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {tiles.map((tile) => (
                        <div key={tile.key} className="rounded-lg border p-3" data-testid={`dp-tile-${tile.key}`}>
                            <div className="text-2xl font-semibold tabular-nums" data-testid={`dp-tile-${tile.key}-count`}>
                                {tile.count ?? '…'}
                            </div>
                            <div className="text-xs text-muted-foreground">{t(`urgencies.${tile.key}`)}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mb-6" data-testid="dp-queue">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('queue.title')}</h2>
                {loading ? (
                    <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                ) : queueEmpty ? (
                    <EmptyState icon={mdiShieldLockOutline} title={t('queue.empty')} data-testid="dp-queue-empty" />
                ) : (
                    <div className="border rounded-lg divide-y">
                        {docsDue.data?.items.map((d) => (
                            <div key={d.uid} className="flex items-center gap-3 px-4 py-2" data-testid={`dp-queue-document-${d.uid}`}>
                                <Badge variant="outline">{t('queue.document')}</Badge>
                                <Link to="/$org/documents/$docUid" params={{ org, docUid: d.uid! }} className="flex-1 truncate hover:underline" data-testid={`dp-queue-document-link-${d.uid}`}>
                                    {d.name}
                                </Link>
                                <RetentionBadge state={d.retention_state} until={d.retention_until} />
                                <ExtendRetentionAction org={org} documentUid={d.uid!} />
                                <DocumentPurgeAction org={org} docUid={d.uid!} origin="retention" compact />
                            </div>
                        ))}
                        {contactsDue.data?.items.map((u) => (
                            <div key={u.uid} className="flex items-center gap-3 px-4 py-2" data-testid={`dp-queue-contact-${u.uid}`}>
                                <Badge variant="outline">{t('queue.contact')}</Badge>
                                <Link to="/$org/users/$userUid" params={{ org, userUid: u.uid! }} className="flex-1 truncate hover:underline" data-testid={`dp-queue-contact-link-${u.uid}`}>
                                    {u.name}
                                </Link>
                                <RetentionBadge state={u.retention_state} until={u.retention_until} />
                                <ExtendRetentionAction org={org} userUid={u.uid!} />
                                <AnonymizeContactAction
                                    org={org}
                                    userUid={u.uid!}
                                    onCreated={() => void navigate({ to: '/$org/users/$userUid', params: { org, userUid: u.uid! } })}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <ErasureRequestsSection org={org} />

            <section data-testid="dp-measures">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('measures.title')}</h2>
                {!measures.data?.length ? (
                    <p className="text-sm text-muted-foreground" data-testid="dp-measures-empty">{t('measures.empty')}</p>
                ) : (
                    <div className="border rounded-lg divide-y">
                        {[...overdueHolds, ...measures.data.filter((m) => !m.review_overdue)].map((m) => (
                            <div key={m.uid} className="flex flex-wrap items-center gap-3 px-4 py-2" data-testid={`dp-measure-${m.uid}`}>
                                <Badge variant={m.kind === 'hold' ? 'info' : 'warning'}>{t(`measures.kind.${m.kind}`)}</Badge>
                                <span className="flex-1 truncate">{m.document_name ?? m.asset_name ?? m.user_name}</span>
                                <span className="text-xs text-muted-foreground">{t(`measures.reason.${m.reason}`)}</span>
                                {m.review_at && (
                                    <Badge variant={m.review_overdue ? 'destructive' : 'secondary'} data-testid={`dp-measure-review-${m.uid}`}>
                                        {t('measures.review_at', { date: fmt(m.review_at) })}
                                    </Badge>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    )
}
